/* « Catégories » and « Emplacements » — the workspace's two other lists, beside the étiquettes (wireframes ruling 20/10 § 6;
   Technical's 0039 and its vet: P1 as built, one value per item). A catégorie files a campaign (never a gift type: the
   Cadeaux filter stays on the gift types' fixed category, Q2 (a)); an emplacement says where a support stands. Both are the
   merchant's own organisation: never shown to a customer, never a rule of the parcours, never in a document, a version, /d,
   play or a till answer. A catégorie's name never speaks of the gift (name_ties_gift); an emplacement's is a place word
   (« caisse », « vitrine ») and has no such check.
   lib-pack-23 · the wire. With an API (VITE_API_URL) each list is the server's (GET /v1/app/categories, /v1/app/places,
   contract 02 §2.6, read with the mirror at the app's root), each change of it one whole-list PUT (lib/writes), « Fusionner »
   one POST …/:id/merge, and an item's value is its row's (`Campaign.categoryId`, `SupportRef.placeId`), written with PATCH
   { categoryId } on the campaign or PATCH { placeId } on the support — row data, never a document, never a version. An API
   without 0039 (staging before the point-2 block) answers both GETs 404: the lists read as not there yet
   (`organisationReady()` false), stay empty, and nothing is said.
   The demo (no VITE_API_URL) keeps both lists and each item's value in one client store per world
   (`bmr.organisation.<world>`); cruising and nine-locations start with examples, which the mock server's database takes
   too (mocks/server-b › seedOrganisation). A support's free « Où » text finds or makes its place on save, as the API's P4
   does (lib/campaign › addSupport). */
import { useSyncExternalStore } from 'react'
import { currentWorld, type World } from '@/mocks/worlds'
import { apiWrites } from './api'
import { GIFT_WORDS, INCENTIVE_WORDS } from './asks'
import { edited, mirror, serverIdOf, setMirror, subscribeMirror } from './mirror'
import { canWriteTagList, cleanTagName, normTagName } from './tags'
import type { CurrentUser } from './scope'

/** One catégorie of the workspace's list (the API's `categories` row). `counts` are the server's (P3: the reader's scope,
    archived campaigns included) and travel only with an API; the demo counts its store (categoryCounts). */
export interface Category { id: string; name: string; position: number; createdAt: string; counts?: { campaigns: number } }
/** One emplacement of the workspace's list (the API's `places` row); `counts` as above, deactivated supports included. */
export interface Place { id: string; name: string; position: number; createdAt: string; counts?: { supports: number } }

/** 0039 · 30 catégories per workspace (PUT → too_many_items), a name of 1–24 characters. */
export const MAX_CATEGORIES = 30
export const CATEGORY_NAME_MAX = 24
/** 0039 · 50 emplacements per workspace (PUT → too_many_items; a support's new « Où » past it → too_many_places), 1–60 characters. */
export const MAX_PLACES = 50
export const PLACE_NAME_MAX = 60

/* the merchant's words for each refusal (⟦confirmable⟧, D-82): the étiquettes' with « catégorie » / « emplacement » */
export const CATEGORY_NAME_TIES_GIFT = 'Le nom de la catégorie ne parle pas du cadeau.'
export const CATEGORY_NAME_EMPTY = 'Donnez un nom à la catégorie.'
export const CATEGORY_NAME_LONG = `Un nom de catégorie tient en ${CATEGORY_NAME_MAX} caractères.`
export const CATEGORY_NAME_TAKEN = 'Une catégorie porte déjà ce nom.'
export const CATEGORY_CAP_LINE = `${MAX_CATEGORIES} catégories au plus : renommez, fusionnez ou supprimez-en une pour en créer une autre.`
export const CATEGORY_ROLE_LINE = 'Votre rôle ne permet pas de gérer les catégories.'
export const CATEGORY_GONE = 'Cette catégorie n’existe plus : rechargez la page.'
export const PLACE_NAME_EMPTY = 'Donnez un nom à l’emplacement.'
export const PLACE_NAME_LONG = `Un nom d’emplacement tient en ${PLACE_NAME_MAX} caractères.`
export const PLACE_NAME_TAKEN = 'Un emplacement porte déjà ce nom.'
/** The API's own line (0039 too_many_places), also the list's cap. */
export const PLACE_CAP_LINE = `Vous avez déjà ${MAX_PLACES} emplacements : choisissez-en un dans la liste.`
export const PLACE_ROLE_LINE = 'Votre rôle ne permet pas de gérer les emplacements.'
export const PLACE_GONE = 'Cet emplacement n’existe plus : rechargez la page.'

/** Both lists and each item's value: a campaign id → its catégorie, a support id → its emplacement. `null` = none, said
    on purpose; an item absent here reads its row (`categoryId`, `placeId`, else a support's « Où » text by name). */
export interface OrgStore { categories: Category[]; places: Place[]; campaigns: Record<string, string | null>; supports: Record<string, string | null> }
type ListKey = 'categories' | 'places'
const key = (w: World = currentWorld()) => `bmr.organisation.${w}`

/** The étiquettes' name key (P2): trim, inner whitespace collapsed (a no-break space reads as one), NFC,
    `toLocaleLowerCase('fr')` — case folds, accents stay significant. The API's for both lists. */
export const normOrgName = normTagName
/** The name as it is stored and shown: trimmed, inner whitespace collapsed, NFC. */
export const cleanOrgName = cleanTagName
const nameLength = (name: string): number => [...name].length

/* ---------- the worlds' examples: cruising and nine-locations show populated lists; the others start empty ---------- */
const SEED_AT = '2026-10-01T09:00:00.000Z'
const EMPTY_STORE: OrgStore = { categories: [], places: [], campaigns: {}, supports: {} }
/** The examples a world starts with, under the demo's ids (the mock server re-keys them as it names its rows). */
export function organisationExamples(w: World): OrgStore {
  if (w !== 'cruising' && w !== 'nine-locations') return EMPTY_STORE
  return {
    categories: [
      { id: 'cat-annee', name: 'Toute l’année', position: 0, createdAt: SEED_AT },
      { id: 'cat-saison', name: 'Saison', position: 1, createdAt: SEED_AT },
      { id: 'cat-evenement', name: 'Événement', position: 2, createdAt: SEED_AT },
    ],
    places: [
      { id: 'pl-comptoir', name: 'Comptoir', position: 0, createdAt: SEED_AT },
      { id: 'pl-vitrine', name: 'Vitrine', position: 1, createdAt: SEED_AT },
    ],
    campaigns: { c1: 'cat-annee', c3: 'cat-saison' },
    /* no fixture support carries a place word, so none starts with an emplacement (as 0039 migrates: only existing words) */
    supports: {},
  }
}

/* ---------- the demo's store (one per world, in this browser) ---------- */
/* one parsed snapshot per (world, stored string), so useSyncExternalStore gets a stable value */
let cache: { k: string; raw: string | null; v: OrgStore } | null = null
function readWorld(): OrgStore {
  const k = key()
  let raw: string | null = null
  try { raw = localStorage.getItem(k) } catch { /* fine */ }
  if (cache && cache.k === k && cache.raw === raw) return cache.v
  let v: OrgStore | null = null
  if (raw) {
    try {
      const p = JSON.parse(raw) as OrgStore
      if (p && Array.isArray(p.categories) && Array.isArray(p.places)) v = { categories: p.categories, places: p.places, campaigns: p.campaigns ?? {}, supports: p.supports ?? {} }
    } catch { /* fine */ }
  }
  cache = { k, raw, v: v ?? organisationExamples(currentWorld()) }
  return cache.v
}
const listeners = new Set<() => void>()
function writeWorld(s: OrgStore): void {
  try { localStorage.setItem(key(), JSON.stringify(s)) } catch { /* fine */ }
  listeners.forEach(l => l())
}
const subscribeWorld = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }

/* ---------- with an API: the tab's copy of the server's (lib/mirror) ---------- */
/* one store object per (categories, places, itemCategory, itemPlace) of the mirror, so useSyncExternalStore gets a stable value */
let apiCache: { c: Category[]; p: Place[]; ic: Record<string, string | null>; ip: Record<string, string | null>; v: OrgStore } | null = null
function readApi(): OrgStore {
  const m = mirror()
  if (apiCache && apiCache.c === m.categories && apiCache.p === m.places && apiCache.ic === m.itemCategory && apiCache.ip === m.itemPlace) return apiCache.v
  apiCache = { c: m.categories, p: m.places, ic: m.itemCategory, ip: m.itemPlace, v: { categories: m.categories, places: m.places, campaigns: m.itemCategory, supports: m.itemPlace } }
  return apiCache.v
}
/** The tab's copy changes at once; what changed is said to lib/writes by the seam that changed it (`edited`). */
const writeApi = (s: OrgStore): void => setMirror({ categories: s.categories, places: s.places, itemCategory: s.campaigns, itemPlace: s.supports })

const read = (): OrgStore => (apiWrites ? readApi() : readWorld())
const write = (s: OrgStore): void => (apiWrites ? writeApi(s) : writeWorld(s))
const subscribe = (l: () => void) => (apiWrites ? subscribeMirror(l) : subscribeWorld(l))
/** An item's key in the store: with an API the server's id (a draft named here, then by the server, reads as one). */
const keyOf = (id: string): string => (apiWrites ? serverIdOf(id) : id)
const byPosition = <T extends { position: number }>(l: T[]): T[] => l.slice().sort((a, b) => a.position - b.position)

/** Whether the lists can be read: the demo always; with an API once the server answered them (an API without 0039 answers
    404, and the sections that use them show nothing new). */
export const organisationReady = (): boolean => !apiWrites || mirror().orgReady

/* ---------- one list (the same rules for both, the gift check aside) ---------- */
const RULES = {
  categories: { max: MAX_CATEGORIES, nameMax: CATEGORY_NAME_MAX, giftCheck: true, empty: CATEGORY_NAME_EMPTY, long: CATEGORY_NAME_LONG, taken: CATEGORY_NAME_TAKEN, cap: CATEGORY_CAP_LINE, prefix: 'cat' },
  places: { max: MAX_PLACES, nameMax: PLACE_NAME_MAX, giftCheck: false, empty: PLACE_NAME_EMPTY, long: PLACE_NAME_LONG, taken: PLACE_NAME_TAKEN, cap: PLACE_CAP_LINE, prefix: 'pl' },
} as const
const ITEMS = { categories: 'campaigns', places: 'supports' } as const

function setList(k: ListKey, list: Array<Category | Place>): void {
  const s = read()
  const items = list.map((t, i) => ({ ...t, name: cleanOrgName(t.name), position: i }))
  const keep = new Set(items.map(t => t.id))
  const ik = ITEMS[k]
  /* an id dropped = « Supprimer »: its items go to none (0039: the join's cascade) */
  const pruned = Object.fromEntries(Object.entries(s[ik]).map(([id, v]) => [id, v && keep.has(v) ? v : null]))
  write({ ...s, [k]: items, [ik]: pruned })
  edited({ kind: k })
}
function mergeList(k: ListKey, from: string, into: string): void {
  if (from === into) return
  const s = read()
  const list = s[k] as Array<Category | Place>
  if (!list.some(t => t.id === from) || !list.some(t => t.id === into)) return
  const ik = ITEMS[k]
  const repointed = Object.fromEntries(Object.entries(s[ik]).map(([id, v]) => [id, v === from ? into : v]))
  const items = byPosition(list.filter(t => t.id !== from)).map((t, i) => ({ ...t, position: i }))
  write({ ...s, [k]: items, [ik]: repointed })
  edited({ kind: k === 'categories' ? 'category-merge' : 'place-merge', from, into })
}
function listIssues(k: ListKey, name: string, list: Array<{ id: string; name: string }>, selfId?: string): string | null {
  const r = RULES[k]
  const clean = cleanOrgName(name)
  if (!clean) return r.empty
  if (nameLength(clean) > r.nameMax) return r.long
  if (r.giftCheck && (GIFT_WORDS.test(clean) || INCENTIVE_WORDS.test(clean))) return CATEGORY_NAME_TIES_GIFT
  if (list.some(t => t.id !== selfId && normOrgName(t.name) === normOrgName(clean))) return r.taken
  if (!selfId && list.length >= r.max) return r.cap
  return null
}
/** A new item's id (the app mints ids, as for the étiquettes; the API takes `[A-Za-z0-9_-]{3,40}`). */
const newOrgId = (k: ListKey) => `${RULES[k].prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
/** An item's value: what this tab holds of it, else its row's (`categoryId` / `placeId`), else (a support) its « Où » text
    read by name — `undefined` when nothing names one. */
function valueOf(k: ListKey, id: string, row?: { categoryId?: string | null; placeId?: string | null; place?: string | null }): string | null {
  const m = read()[ITEMS[k]]
  const kk = keyOf(id)
  if (kk in m) return m[kk]
  if (id in m) return m[id]
  const v = k === 'categories' ? row?.categoryId : row?.placeId
  if (v !== undefined) return v ?? null
  if (k === 'places' && row?.place) { const t = normOrgName(row.place); return getPlaces().find(p => normOrgName(p.name) === t)?.id ?? null }
  return null
}
function setValue(k: ListKey, id: string, value: string | null): string | null {
  const s = read()
  if (value !== null && !(s[k] as Array<{ id: string }>).some(t => t.id === value)) return k === 'categories' ? CATEGORY_GONE : PLACE_GONE
  const ik = ITEMS[k]
  const kk = keyOf(id)
  if (kk in s[ik] && s[ik][kk] === value) return null   /* nothing changes: nothing is written, nothing is sent */
  write({ ...s, [ik]: { ...s[ik], [kk]: value } })
  edited({ kind: k === 'categories' ? 'item-category' : 'item-place', id: kk })
  return null
}
function countsOf(k: ListKey, items: Array<string | { id: string; categoryId?: string | null; placeId?: string | null; place?: string | null }>): Record<string, number> {
  const s = read()
  const list = s[k] as Array<Category | Place>
  if (apiWrites) return Object.fromEntries(list.map(t => [t.id, k === 'categories' ? (t as Category).counts?.campaigns ?? 0 : (t as Place).counts?.supports ?? 0]))
  const out: Record<string, number> = Object.fromEntries(list.map(t => [t.id, 0]))
  const seen = new Set<string>()
  for (const it of items) {
    const row = typeof it === 'string' ? undefined : it
    const id = typeof it === 'string' ? it : it.id
    if (seen.has(id)) continue
    seen.add(id)
    const v = valueOf(k, id, row)
    if (v && v in out) out[v]++
  }
  return out
}

/* ---------- « Catégories » (campaigns) ---------- */
/** The list, in position order. */
export const getCategories = (): Category[] => byPosition(read().categories)
/** The whole list at once (the API's PUT): positions renumbered in the order given; an id dropped = « Supprimer », its
    campaigns go to none. With an API the tab's copy changes now and one PUT sends it (lib/writes › sendCategories). */
export const setCategories = (list: Category[]): void => setList('categories', list)
/** « Fusionner » (POST /v1/app/categories/:id/merge { into }): every campaign of `from` is filed under `into`, then `from`
    goes; the survivor keeps its name and position. */
export const mergeCategory = (from: string, into: string): void => mergeList('categories', from, into)
/** Why this name cannot be saved, in the merchant's words — or null. `selfId` is the catégorie being renamed. Mirrors the
    API's invalid_field · duplicate_name · name_ties_gift · too_many_items. */
export const categoryIssues = (name: string, list: Category[] = getCategories(), selfId?: string): string | null => listIssues('categories', name, list, selfId)
/** Adds one catégorie at the end of the list; returns it, or the refusal line. */
export function addCategory(name: string): Category | string {
  const list = getCategories()
  const issue = categoryIssues(name, list)
  if (issue) return issue
  const c: Category = { id: newOrgId('categories'), name: cleanOrgName(name), position: list.length, createdAt: new Date().toISOString() }
  setCategories([...list, c])
  return c
}
/** A campaign's catégorie, or null. `row` is the campaign the screen already has (its `categoryId`). */
export function campaignCategory(campaignId: string, row?: { categoryId?: string | null }): Category | null {
  const v = valueOf('categories', campaignId, row)
  return v ? getCategories().find(c => c.id === v) ?? null : null
}
/** The id a write sends for this campaign (a draft's first POST), or null. */
export const campaignCategoryId = (campaignId: string): string | null => campaignCategory(campaignId)?.id ?? null
/** Files a campaign (PATCH { categoryId }; null clears): row data, never a version — no « Modifications à publier ».
    Returns CATEGORY_GONE for an id the list no longer has, else null. With an API the tab's copy changes now and
    lib/writes sends it once the campaign is on the server (a draft not saved yet carries it in its first POST instead). */
export const setCampaignCategory = (campaignId: string, categoryId: string | null): string | null => setValue('categories', campaignId, categoryId)
/** P3 · how many campaigns carry each catégorie, among those given (ids, or the rows); with an API the server's counts. */
export function categoryCounts(campaigns: Array<string | { id: string; categoryId?: string | null }>): Record<string, { campaigns: number }> {
  return Object.fromEntries(Object.entries(countsOf('categories', campaigns)).map(([id, n]) => [id, { campaigns: n }]))
}
/** Who writes the list: the étiquettes' rule (the owner, an administrator, a manager over every establishment). */
export const canWriteCategoryList = (locationIds: string[], u?: CurrentUser): boolean => canWriteTagList(locationIds, u)

/* ---------- « Emplacements » (supports) ---------- */
export const getPlaces = (): Place[] => byPosition(read().places)
/** The whole list at once (PUT); an id dropped = « Supprimer », its supports go to none. */
export const setPlaces = (list: Place[]): void => setList('places', list)
/** « Fusionner » (POST /v1/app/places/:id/merge { into }). */
export const mergePlace = (from: string, into: string): void => mergeList('places', from, into)
/** Why this name cannot be saved — or null (no gift check on a place word). */
export const placeIssues = (name: string, list: Place[] = getPlaces(), selfId?: string): string | null => listIssues('places', name, list, selfId)
export function addPlace(name: string): Place | string {
  const list = getPlaces()
  const issue = placeIssues(name, list)
  if (issue) return issue
  const p: Place = { id: newOrgId('places'), name: cleanOrgName(name), position: list.length, createdAt: new Date().toISOString() }
  setPlaces([...list, p])
  return p
}
/** A support's emplacement, or null. `row` is the support the screen already has: its `placeId`, else its « Où » text
    read by name (a support of an API without 0039, or one this browser kept from before). */
export function supportPlace(supportId: string, row?: { placeId?: string | null; place?: string | null }): Place | null {
  const v = valueOf('places', supportId, row)
  return v ? getPlaces().find(p => p.id === v) ?? null : null
}
/** Places a support (PATCH { placeId }; null clears). Returns PLACE_GONE for an id the list no longer has, else null. */
export const setSupportPlace = (supportId: string, placeId: string | null): string | null => setValue('places', supportId, placeId)
/** P4, the demo's: a support saved with an « Où » text takes the list's place of that name, else a new one at the end
    (none past the cap; blank = none), as the API does with `place`. With an API the server does it: nothing here. */
export function placeFromText(supportId: string, text: string | null | undefined): void {
  if (apiWrites) return
  const t = cleanOrgName(text ?? '')
  if (!t) { setValue('places', supportId, null); return }
  const found = getPlaces().find(p => normOrgName(p.name) === normOrgName(t))
  const p = found ?? (nameLength(t) <= PLACE_NAME_MAX ? addPlace(t) : null)
  if (p && typeof p !== 'string') setValue('places', supportId, p.id)
}
/** P3 · how many supports carry each emplacement, among those given (ids, or the rows); with an API the server's counts. */
export function placeCounts(supports: Array<string | { id: string; placeId?: string | null; place?: string | null }>): Record<string, { supports: number }> {
  return Object.fromEntries(Object.entries(countsOf('places', supports)).map(([id, n]) => [id, { supports: n }]))
}
export const canWritePlaceList = (locationIds: string[], u?: CurrentUser): boolean => canWriteTagList(locationIds, u)

/* ---------- with an API: what the server holds of an item ---------- */
/** A campaign row read or answered: its `categoryId`, unless a write asked since will send the tab's own (lib/writes
    checks that). `quiet`: noted during a render — never overrides what the tab holds. */
export function noteCampaignCategory(id: string, categoryId: string | null, quiet = false): void {
  if (!apiWrites) return
  const m = mirror().itemCategory
  if (quiet && id in m) return
  if (id in m && m[id] === categoryId) return
  setMirror({ itemCategory: { ...m, [id]: categoryId } }, quiet)
}
/** A support row read or answered: its `placeId` (absent on an API without 0039: nothing is noted). */
export function noteSupportPlace(id: string, placeId: string | null | undefined, quiet = false): void {
  if (!apiWrites || placeId === undefined) return
  const m = mirror().itemPlace
  if (quiet && id in m) return
  if (id in m && m[id] === placeId) return
  setMirror({ itemPlace: { ...m, [id]: placeId } }, quiet)
}
/** A draft the server has just named: its catégorie reads under the server's id now (lib/campaign › rekeyCampaign). */
export function rekeyCampaignCategory(from: string, to: string): void {
  if (!apiWrites || from === to) return
  const m = mirror().itemCategory
  if (!(from in m) || to in m) return
  const { [from]: v, ...rest } = m
  setMirror({ itemCategory: { ...rest, [to]: v } }, true)
}
/** A copy keeps its catégorie (0039: copied by duplicate). `quiet` copies it in this tab only (lib/campaign ›
    duplicateCampaign): with an API the copy's first POST then carries it (lib/server › saveDraft). */
export function copyCampaignCategory(from: string, to: string, quiet = false): void {
  const v = campaignCategory(from)?.id ?? null
  if (!v) return
  if (!quiet) { setCampaignCategory(to, v); return }
  const s = read(); const k = keyOf(to)
  write({ ...s, campaigns: { ...s.campaigns, [k]: v } })
}

/* ---------- React ---------- */
/** The lists as React values: re-render on every write in this tab (and, with an API, on every answer read back). */
export function useCategories(): Category[] { return byPosition(useSyncExternalStore(subscribe, read, read).categories) }
export function usePlaces(): Place[] { return byPosition(useSyncExternalStore(subscribe, read, read).places) }
/** Re-renders a component when any item's catégorie or emplacement changes (the filters, the chips, the grouping). */
export const useOrganisationStore = (): unknown => useSyncExternalStore(subscribe, read, read)
/** Whether the lists are there (organisationReady), as a hook: a page re-renders when the two GETs land (or never, until
    0039: they answer 404). The demo: always true. */
export const useOrganisationReady = (): boolean => useSyncExternalStore(subscribe, organisationReady, organisationReady)
